/**
 * Shape the engine's per-category breakdowns into the labels + values arrays
 * the chart.js radar dataset wants. Extracted from `SpiderChart.tsx` so the
 * wiring is unit-testable without a render harness.
 *
 * - Order matches `CATEGORY_DISPLAY_ORDER` regardless of what order the
 *   server returns the categories in, so the chart's shape stays stable
 *   across investors.
 * - Categories with no applicable questions (or missing from the result
 *   entirely) render as a 0 axis instead of dropping out — keeps the radar
 *   polygon intact.
 */
import {
  CategoryBreakdown,
  CATEGORY_DISPLAY_ORDER,
  CATEGORY_SHORT_LABELS,
  DealCategory,
} from '../types';

export interface SpiderDatum {
  category: DealCategory;
  label: string;
  /** 0–1 axis value (spec §"Create spider chart"). */
  value: number;
  /** True when this category had no applicable questions for the deal. */
  empty: boolean;
  breakdown: CategoryBreakdown | null;
}

export const buildSpiderData = (
  categories: CategoryBreakdown[]
): SpiderDatum[] => {
  const byCategory = new Map<DealCategory, CategoryBreakdown>();
  for (const cat of categories) byCategory.set(cat.category, cat);

  return CATEGORY_DISPLAY_ORDER.map((category) => {
    const breakdown = byCategory.get(category) ?? null;
    if (!breakdown || breakdown.possible === 0) {
      return {
        category,
        label: CATEGORY_SHORT_LABELS[category],
        value: 0,
        empty: true,
        breakdown,
      };
    }
    return {
      category,
      label: CATEGORY_SHORT_LABELS[category],
      value: breakdown.spiderValue,
      empty: false,
      breakdown,
    };
  });
};
